export default customElements.define('w-radio-list-v2', class RadioList extends wealthify.CustomElementBaseClass {

    constructor() {
        const styleSelector = 'c-radio-list-v2';
        const config = {
            styleSelector,
            templateHTML: `
            <div class="${styleSelector}__wrapper" data-root>
                <div class="${styleSelector}__container" data-optionscontainer></div>
                <w-icon class="${styleSelector}__state-icon" data-stateicon></w-icon>
            </div>`,
            cacheDom: [
                'data-optionscontainer', 'data-stateicon'
            ]
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(`${this.styleSelector}`);

            this.previousValidity = null;

            this.addEventListener('blur', () => {
                if (this.valid) {
                    this.classList.remove(`${this.styleSelector}--valid`);
                    this.dom.stateicon.type = 'no-icon';
                }
            }, true);

            this.addEventListener('invalid', (event) => event.preventDefault(), true);

        };
    }

    static get requiredItemsProperties () {
        return [
            'label',
            'value'
        ];
    }

    static get validWarnings () {
        return [
            'required'
        ];
    }

    static get validLayouts () {
        return [
            'stacked', 'inline'
        ];
    }

    static eventSettings = Object.freeze({events: {ValueChange: 'valuechange'}});


    static get observedProps() {
        return [
            new wealthify.Prop('items', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.ArrayObjectContainsKeys(this.requiredItemsProperties)]
            }),
            new wealthify.Prop('key', { required: true }),
            new wealthify.Prop('layout', {
                default: 'stacked',
                validators: [wealthify.propValidators.IsInArray(this.validLayouts)]
            }),
            new wealthify.Prop('selected'),
            new wealthify.Prop('required', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('warnings', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.IsInArray(this.validWarnings)]
            })
        ];
    }

    get propCallbacks() {
        return {
            items: (value) => {
                this.dom.radiogroups = [];

                value.forEach((item, index) => {
                    this.dom.radiogroups[index] = {};

                    this._createItem(item, index);
                });

                if (this.selected) {
                    this._setSelectedItemByValue();
                }
            },
            key: () => {
                if (!this.dom.radiogroups) {
                    return;
                }
        
                Array.prototype.forEach.call(this.dom.radiogroups, (group, index) => {
                    this._addInputAttributes(index);
                });
            },
            layout: () => {
                this.constructor.validLayouts.forEach((type) => this.classList.remove(`${this.styleSelector}--${type}`));
                this.classList.add(`${this.styleSelector}--${this.layout}`);
            },
            selected: () => {
                if (!this.dom.radiogroups) {
                    return;
                }

                this._setSelectedItemByValue();
                this._fireStateRefresh();
            },
            required: () => {
                const isErrorAWarning = this._checkIsErrorAWarning('required');

                if (isErrorAWarning) {
                    return;
                }
            },
            warnings: () => { }
        };
    }

    get value () {
        return this.selected || undefined;
    }

    get valid () {
        return this.dom.radiogroups ? this.dom.radiogroups[0].input.checkValidity() : undefined;
    }

    _checkIsErrorAWarning (error) {
        return this.warnings !== undefined && wealthify.type.array.includes(error, this.warnings);
    }

    _createItem(item, index) {
        const label = this.dom.radiogroups[index].label = document.createElement('label');

        label.classList.add(`${this.styleSelector}__item`);
        label.setAttribute('data-label', '');

        const input = this.dom.radiogroups[index].input = document.createElement('input');

        input.classList.add(`${this.styleSelector}__item-input`);
        input.type = 'radio';
        input.value = item.value;

        this._addInputAttributes(index);

        if (this.selected === index) {
            input.checked = true;
            this.selected = item.value;
        }

        input.addEventListener('change', (event) => {
            this.selected = event.currentTarget.value;
        });

        input.getWrappingComponent = () => this;

        label.appendChild(input);
        label.insertAdjacentHTML('beforeend', item.label);
        this.dom.optionscontainer.appendChild(label);
    }

    _setSelectedItemByValue() {
        const index = this._getIndexBySelected();

        if (index !== null) {
            this._updateButtons(index);
        }
    }

    _getIndexBySelected () {
        let valueIndex = null;

        this.dom.radiogroups.forEach((radio, index) => {
            if (radio.input.value === this.selected) {
                valueIndex = index;
            }
        });

        return valueIndex;
    }

    _addInputAttributes (index) {
        if (this.key === undefined) {
            return;
        }

        const inputId = `${this.key}-${index}`;

        this.dom.radiogroups[index].input.name = this.key;
        this.dom.radiogroups[index].input.id = inputId;
        this.dom.radiogroups[index].label.setAttribute('for', inputId);

        if (this.required) {
            this.dom.radiogroups[index].input.setAttribute('required', '');
        }
    }

    _updateButtons(index) {
        Array.prototype.forEach.call(this.dom.radiogroups, (group) => {
            group.label.classList.remove(`${this.styleSelector}__item--active`);
            group.input.classList.remove(`${this.styleSelector}__item-input--active`);
            group.input.checked = false;
        });

        this.dom.radiogroups[index].label.classList.add(`${this.styleSelector}__item--active`);
        this.dom.radiogroups[index].input.classList.add(`${this.styleSelector}__item-input--active`);
        this.dom.radiogroups[index].input.checked = true;

        this.hasWarning = false;
        
        if (this.warnings) {
            this._checkAndSetWarning();
        }
    }

    _checkAndSetWarning () {
        this._addWarningAttributes();

        const errorMap = { valueMissing: 'required' };

        for (let error in errorMap) {
            const currentErrors = this.dom.radiogroups[0].input.validity;
            const errorIsOverwritten = this.warnings.some((warning) => warning === errorMap[error]);

            if (currentErrors[error] && errorIsOverwritten) {
                this.hasWarning = true;
            }
        }

        this._removeWarningAttributes();
    }

    _addWarningAttributes () {
        this.warnings.forEach((warning) => {
            this.dom.radiogroups[0].input.setAttribute(warning, this[warning]);
        });
    }

    _removeWarningAttributes () {
        this.warnings.forEach((warning) => {
            this.dom.radiogroups[0].input.removeAttribute(warning);
        });
    }

    _dispatchEvent () {
        const event = document.createEvent('Event');
                        
        event.initEvent('valuechange', true, true);
        this.dispatchEvent(event);
    }

    _toggleState () {
        const wasInvalidated = this.valid === false;
        const wasCorrected = this.previousValidity == false && this.valid;
        const remainsCorrect = this.previousValidity && this.valid;
        const validClassListAction = wasCorrected || remainsCorrect ? 'add' : 'remove';
        const invalidClassListAction = wasInvalidated ? 'add' : 'remove';

        let icon = wasInvalidated ? 'danger-outlined' : 'no-icon';

        if (remainsCorrect) {
            return;
        }

        if (wasCorrected) {
            icon = 'success-outlined';
        }
        
        this.classList[validClassListAction](`${this.styleSelector}--valid`);
        this.classList[invalidClassListAction](`${this.styleSelector}--invalid`);
        this.dom.stateicon.type = icon;
    }

    _fireStateRefresh () {
        this._toggleState();
        this.events.ValueChange.dispatch();

        this.previousValidity = this.valid;
    }

    fireValidation () {
        this._fireStateRefresh();
    }
});